// Quality Score system for Client Settings.
// Each field has a fixed weight. Sum is exactly 100.

import type { FormState } from "./settingsForm";

export interface ScoreWeights {
  [fieldName: string]: number;
}

// User-confirmed weights, total = 100.
// Re-weighted (chunk3z-fix82) for the Core 30 layout: GBP is the highest-
// leverage local-relevance signal so primary category + service list carry
// real weight; Brand & CTA is now a live section (hero tagline + CTA) and
// gets light polish weight; image/prompt customization stay unweighted
// (they don't change base content quality).
// fix269: added writingVoiceProfile (the single biggest content-quality lever).
// fix275: the score now measures CONTENT QUALITY ONLY. Removed fields that exist
// for webpage-building/branding the team isn't using (website crawl URL, logo,
// brand colors, hero tagline, CTA text, ratings/review counts, phone — phone is
// stripped from content per fix265). Only fields that influence the generated
// article text are weighted, and they total exactly 100, so "To reach 100%" can
// list every remaining content gap and have it sum to the real score gap.
export const FIELD_WEIGHTS: ScoreWeights = {
  businessName: 3,
  businessAddress: 6,
  gbpPrimaryCategory: 6,
  gbpSecondaryCategoriesText: 2,
  gbpServicesText: 12,
  aboutBusiness: 13,
  writingVoiceProfile: 13,
  uniqueSellingPoints: 10,
  credentials: 6,
  voiceTones: 4,
  pointOfView: 2,
  wordsToAlwaysUse: 2,
  wordsToNeverUse: 2,
  targetAudience: 9,
  writingSamples: 2,
  localDetails: 6,
  specificRequests: 1,
  externalLinkDomains: 1,
  // NOT weighted (don't affect generated content): websiteUrl, phoneNumber,
  // brandPrimaryColor, brandAccentColor, heroTagline, ctaText, businessLogoUrl,
  // averageRating, totalReviewCount, socialProfiles, defaultImageStyle,
  // imageStyleNotes, customContentPrompt, writingModel, page-template fields.
};

// Required fields - if missing, shown in the "missing required fields" callout.
// Required fields ALSO get full score weight when filled.
export const REQUIRED_FIELDS: { key: keyof FormState; label: string }[] = [
  { key: "businessName", label: "Business Name" },
  { key: "websiteUrl", label: "Website URL" },
  { key: "businessAddress", label: "Business Address" },
  { key: "gbpPrimaryCategory", label: "GBP Primary Category" },
  { key: "aboutBusiness", label: "About the Business" },
  { key: "voiceTones", label: "Voice Tones" },
  { key: "pointOfView", label: "Point of View" },
  { key: "targetAudience", label: "Target Audience" },
];

// fix274: fields whose value is a "pick" or short token, NOT prose — these are
// complete the moment they're non-empty, so the 10-char "partial" heuristic
// must NOT apply (e.g. primary category "Dentist" is 7 chars but fully valid).
const SHORT_VALUE_FIELDS = new Set<string>([
  "gbpPrimaryCategory",
  "pointOfView",
  "businessName",
  "phoneNumber",
  "brandPrimaryColor",
  "businessLogoUrl",
  "averageRating",
  "totalReviewCount",
  "ctaText",
  "heroTagline",
]);

export function isFieldFilled(
  value: unknown,
  fieldKey?: string
): "full" | "partial" | "empty" {
  if (value instanceof Set) {
    return value.size > 0 ? "full" : "empty";
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? "full" : "empty";
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return "empty";
    // Short-value / "pick" fields are complete as soon as they're non-empty.
    if (fieldKey && SHORT_VALUE_FIELDS.has(fieldKey)) return "full";
    if (trimmed.length < 10) return "partial";
    return "full";
  }
  if (typeof value === "number") {
    return Number.isFinite(value) && value !== 0 ? "full" : "empty";
  }
  return "empty";
}

export function calculateQualityScore(form: FormState): number {
  let total = 0;
  for (const [field, weight] of Object.entries(FIELD_WEIGHTS)) {
    const value = (form as Record<string, unknown>)[field];
    const status = isFieldFilled(value, field);
    if (status === "full") total += weight;
    else if (status === "partial") total += weight * 0.5;
  }
  return Math.min(100, Math.round(total));
}

export function findMissingRequiredFields(form: FormState): string[] {
  const missing: string[] = [];
  for (const { key, label } of REQUIRED_FIELDS) {
    const value = form[key];
    if (isFieldFilled(value, key as string) === "empty") {
      missing.push(label);
    }
  }
  return missing;
}

// =========================================================================
// fix269: "To reach 100%" gaps
// =========================================================================

/**
 * Human-readable labels + a short "what it needs" hint for the weighted fields,
 * used by the "To reach 100%" panel. Only fields that meaningfully move content
 * quality are listed here; trivial 1-point polish fields are intentionally
 * excluded so the panel surfaces the high-impact gaps, not noise.
 */
const GAP_FIELD_META: Partial<
  Record<keyof FormState, { label: string; hint: string }>
> = {
  aboutBusiness: { label: "About Business", hint: "needs 50+ words" },
  writingVoiceProfile: {
    label: "Writing Instructions",
    hint: "needs 50+ words",
  },
  gbpServicesText: { label: "GBP Services", hint: "list your services" },
  uniqueSellingPoints: { label: "Unique Selling Points", hint: "needs 20+ words" },
  targetAudience: { label: "Target Audience", hint: "needs 20+ words" },
  gbpPrimaryCategory: { label: "GBP Primary Category", hint: "pick one" },
  localDetails: { label: "Local Details", hint: "needs 20+ words" },
  credentials: { label: "Credentials", hint: "awards, memberships" },
  businessAddress: { label: "Business Address", hint: "full street address" },
  businessName: { label: "Business Name", hint: "the practice name" },
  voiceTones: { label: "Voice & Tone", hint: "pick up to 2" },
  specificRequests: { label: "Specific Requests", hint: "optional notes" },
  gbpSecondaryCategoriesText: {
    label: "GBP Secondary Categories",
    hint: "optional",
  },
  wordsToAlwaysUse: { label: "Words to Always Use", hint: "optional" },
  wordsToNeverUse: { label: "Words to Never Use", hint: "optional" },
  pointOfView: { label: "Point of View", hint: "we / you / company" },
  writingSamples: { label: "Writing Samples", hint: "paste example copy" },
  externalLinkDomains: { label: "External Links", hint: "authority domains" },
};

export interface ScoreGap {
  key: string;
  label: string;
  hint: string;
  /** Points still available from this field (full weight if empty, half if partial). */
  points: number;
  status: "empty" | "partial";
}

/**
 * fix269: compute the list of fields that still have points available, sorted by
 * impact (points) descending — so the user sees the highest-leverage gaps first.
 * Partial fields contribute half their remaining weight (matching the score
 * math, where partial = 50% credit). Fields not in GAP_FIELD_META (trivial
 * 1-point polish) are omitted to keep the panel focused.
 */
export function getScoreGaps(form: FormState): ScoreGap[] {
  const gaps: ScoreGap[] = [];
  for (const [field, weight] of Object.entries(FIELD_WEIGHTS)) {
    const meta = GAP_FIELD_META[field as keyof FormState];
    if (!meta) continue; // skip trivial polish fields
    const value = (form as Record<string, unknown>)[field];
    const status = isFieldFilled(value, field);
    if (status === "full") continue;
    const points = status === "partial" ? Math.round(weight * 0.5) : weight;
    if (points <= 0) continue;
    gaps.push({
      key: field,
      label: meta.label,
      hint: meta.hint,
      points,
      status: status === "partial" ? "partial" : "empty",
    });
  }
  // Highest impact first; stable tiebreak by label.
  gaps.sort((a, b) => b.points - a.points || a.label.localeCompare(b.label));
  return gaps;
}

// =========================================================================
// Score band → message + color
// =========================================================================
export interface ScoreBand {
  label: string;
  detail: string;
  color: "danger" | "warning" | "success";
  /** Tailwind class name for the filled portion of the bar. */
  barColor: string;
  /** Inline RGB color for the bar — we interpolate red → orange → light green → green. */
  barColorRgb: string;
  /**
   * Inline RGB color for the band label text (e.g. "Good", "Strong profile").
   * Matches barColorRgb for visual consistency, but slightly darker on the
   * green end so it stays readable on white background. The bar uses pure
   * interpolated color; the label uses a readability-tweaked variant.
   */
  labelColorRgb: string;
}

/**
 * Smoothly interpolate a color from red (0%) → orange (~33%) → light green (~66%) → green (100%).
 * Returns a CSS rgb(...) string. Works as inline style so we don't have to ship
 * 100 Tailwind utility classes for every percent value.
 */
function interpolateScoreColor(score: number): string {
  const s = Math.max(0, Math.min(100, score));
  // Anchor stops in [r, g, b]:
  //   0  → red          rgb(220,  38,  38)  (Tailwind danger-500-ish)
  //  33  → orange       rgb(234,  88,  12)  (Tailwind orange-600)
  //  66  → light green  rgb(132, 204,  22)  (Tailwind lime-500)
  // 100  → green        rgb( 22, 163,  74)  (Tailwind success-600)
  const stops = [
    { p: 0,   r: 220, g:  38, b:  38 },
    { p: 33,  r: 234, g:  88, b:  12 },
    { p: 66,  r: 132, g: 204, b:  22 },
    { p: 100, r:  22, g: 163, b:  74 },
  ];
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i]!;
    const b = stops[i + 1]!;
    if (s >= a.p && s <= b.p) {
      const t = (s - a.p) / (b.p - a.p);
      const r = Math.round(a.r + (b.r - a.r) * t);
      const g = Math.round(a.g + (b.g - a.g) * t);
      const bl = Math.round(a.b + (b.b - a.b) * t);
      return `rgb(${r}, ${g}, ${bl})`;
    }
  }
  return "rgb(22, 163, 74)";
}

/**
 * Like interpolateScoreColor but tweaked for text readability on white
 * background. The bar uses bright/saturated colors that look great as a
 * filled rectangle; the label needs slightly darker variants so a single
 * word ("Good") remains readable.
 */
function interpolateLabelColor(score: number): string {
  const s = Math.max(0, Math.min(100, score));
  const stops = [
    // Same hue progression as the bar but pulled toward darker shades.
    { p: 0,   r: 185, g:  28, b:  28 }, // red-700
    { p: 33,  r: 194, g:  65, b:  12 }, // orange-700
    { p: 66,  r: 101, g: 163, b:  13 }, // lime-700
    { p: 100, r:  21, g: 128, b:  61 }, // green-700
  ];
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i]!;
    const b = stops[i + 1]!;
    if (s >= a.p && s <= b.p) {
      const t = (s - a.p) / (b.p - a.p);
      const r = Math.round(a.r + (b.r - a.r) * t);
      const g = Math.round(a.g + (b.g - a.g) * t);
      const bl = Math.round(a.b + (b.b - a.b) * t);
      return `rgb(${r}, ${g}, ${bl})`;
    }
  }
  return "rgb(21, 128, 61)";
}

export function getScoreBand(score: number, missingRequired: string[]): ScoreBand {
  // Always show missing required first if any
  if (missingRequired.length > 0) {
    return {
      label: "Missing required fields",
      detail: missingRequired.join(", ") + ".",
      color: "danger",
      barColor: "bg-danger",
      barColorRgb: interpolateScoreColor(0),
      labelColorRgb: interpolateLabelColor(0),
    };
  }

  const barColorRgb = interpolateScoreColor(score);
  const labelColorRgb = interpolateLabelColor(score);

  if (score < 30) {
    return {
      label: "Basic setup only",
      detail:
        "Content will be generic. Fill in About Business and Writing Instructions for much better output.",
      color: "danger",
      barColor: "bg-danger",
      barColorRgb,
      labelColorRgb,
    };
  }
  if (score < 60) {
    return {
      label: "Decent",
      detail:
        "Content will be okay. Add more detail to About Business, Voice & Tone, and Audience for stronger output.",
      color: "warning",
      barColor: "bg-warning",
      barColorRgb,
      labelColorRgb,
    };
  }
  if (score < 85) {
    return {
      label: "Good",
      detail:
        "Content will be solid. A few more details would help further. Consider adding writing samples or specific requests.",
      color: "warning",
      barColor: "bg-warning",
      barColorRgb,
      labelColorRgb,
    };
  }
  return {
    label: "Strong profile",
    detail: "Your content will reflect your business voice and local expertise.",
    color: "success",
    barColor: "bg-success",
    barColorRgb,
    labelColorRgb,
  };
}
